import { useState, useEffect } from "react";
import { db } from "./firebase";
import { doc, setDoc, getDoc, collection, getDocs, query, orderBy, limit, updateDoc, deleteDoc } from "firebase/firestore";

const CLOUD = "dsczkplah";
const PRESET = "mymusic_unsigned";

async function uploadToCloudinary(file, type = "auto") {
  const form = new FormData();
  form.append("file", file);
  form.append("upload_preset", PRESET);
  const r = await fetch(`https://api.cloudinary.com/v1_1/${CLOUD}/${type}/upload`, { method: "POST", body: form });
  const d = await r.json();
  return d.secure_url;
}

export default function ArtistProfile({ user, onBack, onPlaySong, currentSong }) {
  const [tab, setTab] = useState("overview");
  const [profile, setProfile] = useState(null);
  const [songs, setSongs] = useState([]);
  const [fans, setFans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [success, setSuccess] = useState("");

  // Edit form state
  const [artistName, setArtistName] = useState("");
  const [bio, setBio] = useState("");
  const [genre, setGenre] = useState("");
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState("");
  const [coverFile, setCoverFile] = useState(null);
  const [coverPreview, setCoverPreview] = useState("");

  // Upload song state
  const [showUpload, setShowUpload] = useState(false);
  const [songTitle, setSongTitle] = useState("");
  const [songFile, setSongFile] = useState(null);
  const [songCoverFile, setSongCoverFile] = useState(null);
  const [songCoverPreview, setSongCoverPreview] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState("");

  const genres = ["Pop", "Hip-Hop", "R&B", "Rock", "Jazz", "Electronic", "K-Pop", "Myanmar Pop", "Myanmar Rock", "Christian", "Indie", "Other"];

  useEffect(() => { loadAll(); }, []);

  async function loadAll() {
    setLoading(true);
    try {
      const [profileSnap, songsSnap, fansSnap] = await Promise.all([
        getDoc(doc(db, "artists", user.uid)),
        getDocs(query(collection(db, "songs"), orderBy("createdAt", "desc"), limit(50))),
        getDocs(query(collection(db, "artists", user.uid, "topListeners"), orderBy("plays", "desc"), limit(10))),
      ]);

      if (profileSnap.exists()) {
        const d = profileSnap.data();
        setProfile(d);
        setArtistName(d.artistName || "");
        setBio(d.bio || "");
        setGenre(d.genre || "");
        setPhotoPreview(d.photoUrl || "");
        setCoverPreview(d.coverUrl || "");
      }

      const mySongs = songsSnap.docs.map(d => ({ id: d.id, ...d.data() })).filter(s => s.artistId === user.uid && !s.hidden);
      setSongs(mySongs);
      setFans(fansSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch {}
    setLoading(false);
  }

  async function saveProfile() {
    setSaving(true);
    try {
      let photoUrl = profile?.photoUrl || "";
      let coverUrl = profile?.coverUrl || "";
      if (photoFile) photoUrl = await uploadToCloudinary(photoFile, "image");
      if (coverFile) coverUrl = await uploadToCloudinary(coverFile, "image");
      await setDoc(doc(db, "artists", user.uid), { artistName, bio, genre, photoUrl, coverUrl, userId: user.uid, updatedAt: new Date().toISOString() }, { merge: true });
      setProfile(prev => ({ ...prev, artistName, bio, genre, photoUrl, coverUrl }));
      setSuccess("Profile updated!");
      setEditing(false);
      setTimeout(() => setSuccess(""), 3000);
    } catch {}
    setSaving(false);
  }

  async function uploadSong() {
    if (!songTitle.trim() || !songFile) return;
    setUploading(true);
    try {
      setUploadProgress("Uploading audio...");
      const audioUrl = await uploadToCloudinary(songFile, "video");
      let coverUrl = "";
      if (songCoverFile) {
        setUploadProgress("Uploading cover...");
        coverUrl = await uploadToCloudinary(songCoverFile, "image");
      }
      setUploadProgress("Saving...");
      const newSong = {
        title: songTitle.trim(),
        artistId: user.uid,
        artistName: profile?.artistName || user.displayName || "Artist",
        artistPhoto: profile?.photoUrl || user.photoURL || "",
        audioUrl,
        coverUrl,
        genre,
        likes: 0, plays: 0, comments: 0,
        createdAt: new Date().toISOString(),
        uid: user.uid,
        artistVerified: profile?.verified || false,
      };
      const { addDoc } = await import("firebase/firestore");
      await addDoc(collection(db, "songs"), newSong);
      setSongs(prev => [{ id: Date.now().toString(), ...newSong }, ...prev]);
      setSongTitle(""); setSongFile(null); setSongCoverFile(null); setSongCoverPreview("");
      setShowUpload(false);
      setSuccess("Song uploaded!");
      setTimeout(() => setSuccess(""), 3000);
    } catch { setUploadProgress("Upload failed. Try again."); }
    setUploading(false); setUploadProgress("");
  }

  async function deleteSong(songId) {
    if (!window.confirm("Delete this song?")) return;
    try {
      await deleteDoc(doc(db, "songs", songId));
      setSongs(prev => prev.filter(s => s.id !== songId));
    } catch {}
  }

  // Analytics calculations
  const totalPlays = songs.reduce((a, s) => a + (s.plays || 0), 0);
  const totalLikes = songs.reduce((a, s) => a + (s.likes || 0), 0);
  const totalComments = songs.reduce((a, s) => a + (s.comments || 0), 0);
  const topSong = songs.sort((a, b) => (b.plays || 0) - (a.plays || 0))[0];
  const thisMonth = songs.filter(s => new Date(s.createdAt).getMonth() === new Date().getMonth());
  const monthPlays = thisMonth.reduce((a, s) => a + (s.plays || 0), 0);

  const TABS = [
    { id: "overview", label: "Overview" },
    { id: "songs", label: "Songs" },
    { id: "analytics", label: "Analytics" },
    { id: "fans", label: "Top Fans" },
    { id: "edit", label: "Edit Profile" },
  ];

  if (loading) return (
    <div style={{ position: "fixed", inset: 0, background: "#000", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 200 }}>
      <div style={{ width: 36, height: 36, border: "3px solid #222", borderTopColor: "#1db954", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
    </div>
  );

  return (
    <div style={{ position: "fixed", inset: 0, background: "#000", color: "#fff", fontFamily: "'DM Sans',sans-serif", overflowY: "auto", zIndex: 200, paddingBottom: 80 }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Syne:wght@700;800&family=DM+Sans:wght@400;500;600&display=swap'); @keyframes spin{to{transform:rotate(360deg)}}`}</style>

      {/* Cover Banner */}
      <div style={{ height: 140, background: coverPreview ? `url(${coverPreview}) center/cover no-repeat` : "linear-gradient(135deg, #0d2818, #1a4a2a)", position: "relative" }}>
        <button onClick={onBack} style={{ position: "absolute", top: 16, left: 16, background: "rgba(0,0,0,0.5)", border: "none", color: "#fff", width: 34, height: 34, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
          <svg viewBox="0 0 24 24" style={{ width: 14, height: 14, fill: "none", stroke: "#fff", strokeWidth: 2 }}><polyline points="15 18 9 12 15 6"/></svg>
        </button>
        {profile?.verified && (
          <div style={{ position: "absolute", top: 16, right: 16, background: "#1db954", color: "#000", fontSize: 11, fontWeight: 700, padding: "4px 10px", borderRadius: 50 }}>✓ VERIFIED</div>
        )}
      </div>

      {/* Profile Info */}
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "0 16px 16px", marginTop: -40 }}>
        <div style={{ width: 80, height: 80, borderRadius: "50%", border: "3px solid #000", background: "#111", overflow: "hidden", marginBottom: 10, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          {photoPreview ? <img src={photoPreview} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <span style={{ fontSize: 32 }}>🎤</span>}
        </div>
        <div style={{ fontFamily: "'Syne',sans-serif", fontSize: 22, fontWeight: 800, marginBottom: 4 }}>{profile?.artistName || user.displayName || "Artist"}</div>
        <div style={{ fontSize: 13, color: "#555", marginBottom: 12 }}>@{(profile?.artistName || user.displayName || "artist").toLowerCase().replace(/\s+/g, "")} · {profile?.genre || "Music"}</div>

        {/* Stats row */}
        <div style={{ display: "flex", gap: 24, marginBottom: 16 }}>
          {[["Followers", profile?.followers || 0], ["Songs", songs.length], ["Plays", totalPlays.toLocaleString()]].map(([label, val]) => (
            <div key={label} style={{ textAlign: "center" }}>
              <div style={{ fontFamily: "'Syne',sans-serif", fontSize: 18, fontWeight: 800 }}>{val}</div>
              <div style={{ fontSize: 11, color: "#555" }}>{label}</div>
            </div>
          ))}
        </div>

        {success && <div style={{ background: "rgba(29,185,84,0.15)", border: "1px solid #1db954", borderRadius: 10, padding: "8px 16px", fontSize: 13, color: "#1db954", marginBottom: 12 }}>{success}</div>}

        <button onClick={() => setShowUpload(true)} style={{ width: "100%", padding: "11px", borderRadius: 50, border: "none", background: "#1db954", color: "#000", fontSize: 13, fontWeight: 700, cursor: "pointer", marginBottom: 8 }}>
          + Upload Song
        </button>
      </div>

      {/* Tabs */}
      <div style={{ display: "flex", overflowX: "auto", borderBottom: "1px solid #111", padding: "0 16px" }}>
        {TABS.map(t => (
          <div key={t.id} onClick={() => setTab(t.id)} style={{ padding: "10px 14px", fontSize: 13, fontWeight: tab === t.id ? 700 : 400, color: tab === t.id ? "#1db954" : "#555", borderBottom: tab === t.id ? "2px solid #1db954" : "2px solid transparent", cursor: "pointer", flexShrink: 0, whiteSpace: "nowrap" }}>
            {t.label}
          </div>
        ))}
      </div>

      <div style={{ padding: 16 }}>

        {/* OVERVIEW TAB */}
        {tab === "overview" && (
          <div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 16 }}>
              {[
                { label: "Total Plays", value: totalPlays.toLocaleString(), color: "#1db954" },
                { label: "Total Likes", value: totalLikes, color: "#e91429" },
                { label: "Comments", value: totalComments, color: "#4776e6" },
                { label: "This Month", value: monthPlays.toLocaleString(), color: "#f59e0b" },
              ].map(({ label, value, color }) => (
                <div key={label} style={{ background: "#111", borderRadius: 12, padding: 14, border: "1px solid #1a1a1a" }}>
                  <div style={{ fontSize: 11, color: "#555", marginBottom: 4 }}>{label}</div>
                  <div style={{ fontFamily: "'Syne',sans-serif", fontSize: 22, fontWeight: 800, color }}>{value}</div>
                </div>
              ))}
            </div>

            {topSong && (
              <div style={{ background: "#111", borderRadius: 14, padding: 14, border: "1px solid #1a1a1a", marginBottom: 16 }}>
                <div style={{ fontSize: 11, color: "#555", marginBottom: 10, textTransform: "uppercase", letterSpacing: "0.08em" }}>🏆 Top Song</div>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <div style={{ width: 48, height: 48, borderRadius: 8, background: "#222", overflow: "hidden", flexShrink: 0 }}>
                    {topSong.coverUrl ? <img src={topSong.coverUrl} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20 }}>🎵</div>}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 14, fontWeight: 700 }}>{topSong.title}</div>
                    <div style={{ fontSize: 12, color: "#555" }}>{topSong.plays || 0} plays · {topSong.likes || 0} likes</div>
                  </div>
                  <button onClick={() => onPlaySong && onPlaySong({ trackId: topSong.id, trackName: topSong.title, artistName: topSong.artistName, artworkUrl100: topSong.coverUrl || "", audioUrl: topSong.audioUrl, isOriginal: true }, songs.map(s => ({ trackId: s.id, trackName: s.title, artistName: s.artistName, artworkUrl100: s.coverUrl || "", audioUrl: s.audioUrl, isOriginal: true })))} style={{ background: "#1db954", border: "none", borderRadius: "50%", width: 36, height: 36, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
                    <svg viewBox="0 0 24 24" style={{ width: 14, height: 14, fill: "#000" }}><polygon points="5 3 19 12 5 21 5 3"/></svg>
                  </button>
                </div>
              </div>
            )}

            <div style={{ background: "#111", borderRadius: 14, padding: 14, border: "1px solid #1a1a1a" }}>
              <div style={{ fontSize: 11, color: "#555", marginBottom: 10, textTransform: "uppercase", letterSpacing: "0.08em" }}>Bio</div>
              <div style={{ fontSize: 14, color: profile?.bio ? "#ccc" : "#444", lineHeight: 1.6 }}>{profile?.bio || "No bio yet. Edit your profile to add one."}</div>
            </div>
          </div>
        )}

        {/* SONGS TAB */}
        {tab === "songs" && (
          <div>
            <div style={{ fontFamily: "'Syne',sans-serif", fontSize: 16, fontWeight: 800, marginBottom: 14 }}>Your Songs ({songs.length})</div>
            {songs.length === 0 ? (
              <div style={{ textAlign: "center", padding: "40px 20px" }}>
                <div style={{ fontSize: 48, marginBottom: 12 }}>🎵</div>
                <div style={{ fontSize: 14, color: "#555" }}>No songs yet. Upload your first song!</div>
                <button onClick={() => setShowUpload(true)} style={{ marginTop: 16, padding: "12px 24px", borderRadius: 50, border: "none", background: "#1db954", color: "#000", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>+ Upload Song</button>
              </div>
            ) : songs.map((s, i) => (
              <div key={s.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 0", borderBottom: "1px solid #0a0a0a" }}>
                <div style={{ width: 44, height: 44, borderRadius: 8, background: "#111", overflow: "hidden", flexShrink: 0, cursor: "pointer" }} onClick={() => onPlaySong && onPlaySong({ trackId: s.id, trackName: s.title, artistName: s.artistName, artworkUrl100: s.coverUrl || "", audioUrl: s.audioUrl, isOriginal: true }, [])}>
                  {s.coverUrl ? <img src={s.coverUrl} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20 }}>🎵</div>}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{s.title}</div>
                  <div style={{ fontSize: 12, color: "#555" }}>{s.plays || 0} plays · {s.likes || 0} likes · {s.comments || 0} comments</div>
                </div>
                <button onClick={() => deleteSong(s.id)} style={{ background: "none", border: "none", color: "#444", cursor: "pointer", fontSize: 16, padding: 4 }}>🗑️</button>
              </div>
            ))}
          </div>
        )}

        {/* ANALYTICS TAB */}
        {tab === "analytics" && (
          <div>
            <div style={{ fontFamily: "'Syne',sans-serif", fontSize: 16, fontWeight: 800, marginBottom: 14 }}>Analytics</div>

            <div style={{ background: "#111", borderRadius: 14, padding: 16, border: "1px solid #1a1a1a", marginBottom: 12 }}>
              <div style={{ fontSize: 12, color: "#555", marginBottom: 12, textTransform: "uppercase", letterSpacing: "0.08em" }}>Performance Summary</div>
              {[
                { label: "Total Plays", value: totalPlays.toLocaleString(), icon: "▶", color: "#1db954" },
                { label: "Total Likes", value: totalLikes, icon: "♥", color: "#e91429" },
                { label: "Total Comments", value: totalComments, icon: "💬", color: "#4776e6" },
                { label: "Songs Uploaded", value: songs.length, icon: "🎵", color: "#f59e0b" },
                { label: "Avg Plays/Song", value: songs.length > 0 ? Math.round(totalPlays / songs.length) : 0, icon: "📊", color: "#8b5cf6" },
              ].map(({ label, value, icon, color }) => (
                <div key={label} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 0", borderBottom: "1px solid #0a0a0a" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <span style={{ fontSize: 16 }}>{icon}</span>
                    <span style={{ fontSize: 13, color: "#ccc" }}>{label}</span>
                  </div>
                  <span style={{ fontSize: 15, fontWeight: 700, color }}>{value}</span>
                </div>
              ))}
            </div>

            <div style={{ background: "#111", borderRadius: 14, padding: 16, border: "1px solid #1a1a1a", marginBottom: 12 }}>
              <div style={{ fontSize: 12, color: "#555", marginBottom: 12, textTransform: "uppercase", letterSpacing: "0.08em" }}>Top Songs by Plays</div>
              {songs.slice(0, 5).sort((a, b) => (b.plays || 0) - (a.plays || 0)).map((s, i) => (
                <div key={s.id} style={{ marginBottom: 12 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                    <span style={{ fontSize: 13, color: "#ccc", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "70%" }}>{i + 1}. {s.title}</span>
                    <span style={{ fontSize: 13, color: "#1db954", fontWeight: 700 }}>{s.plays || 0}</span>
                  </div>
                  <div style={{ background: "#1a1a1a", borderRadius: 4, height: 4 }}>
                    <div style={{ background: "#1db954", height: "100%", borderRadius: 4, width: `${totalPlays > 0 ? ((s.plays || 0) / totalPlays) * 100 : 0}%`, transition: "width 0.5s" }} />
                  </div>
                </div>
              ))}
              {songs.length === 0 && <div style={{ textAlign: "center", color: "#555", fontSize: 13, padding: "20px 0" }}>Upload songs to see analytics</div>}
            </div>

            <div style={{ background: "#111", borderRadius: 14, padding: 16, border: "1px solid #1a1a1a" }}>
              <div style={{ fontSize: 12, color: "#555", marginBottom: 12, textTransform: "uppercase", letterSpacing: "0.08em" }}>Engagement Rate</div>
              {songs.length > 0 ? (
                <div style={{ textAlign: "center" }}>
                  <div style={{ fontFamily: "'Syne',sans-serif", fontSize: 40, fontWeight: 800, color: "#1db954" }}>
                    {totalPlays > 0 ? ((totalLikes / totalPlays) * 100).toFixed(1) : 0}%
                  </div>
                  <div style={{ fontSize: 13, color: "#555", marginTop: 4 }}>Likes per play ratio</div>
                  <div style={{ fontSize: 12, color: "#333", marginTop: 8 }}>Industry average: 3-5%</div>
                </div>
              ) : <div style={{ textAlign: "center", color: "#555", fontSize: 13, padding: "20px 0" }}>No data yet</div>}
            </div>
          </div>
        )}

        {/* TOP FANS TAB */}
        {tab === "fans" && (
          <div>
            <div style={{ fontFamily: "'Syne',sans-serif", fontSize: 16, fontWeight: 800, marginBottom: 4 }}>Top Fans</div>
            <div style={{ fontSize: 12, color: "#555", marginBottom: 16 }}>Your most loyal listeners</div>
            {fans.length === 0 ? (
              <div style={{ textAlign: "center", padding: "40px 20px" }}>
                <div style={{ fontSize: 48, marginBottom: 12 }}>🏆</div>
                <div style={{ fontSize: 14, color: "#555" }}>No listeners yet — share your music to get fans!</div>
              </div>
            ) : fans.map((fan, i) => (
              <div key={fan.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 0", borderBottom: "1px solid #0a0a0a" }}>
                <div style={{ width: 28, textAlign: "center", fontFamily: "'Syne',sans-serif", fontSize: 16, fontWeight: 800, color: i === 0 ? "#ffd700" : i === 1 ? "#c0c0c0" : i === 2 ? "#cd7f32" : "#444" }}>
                  {i === 0 ? "👑" : i + 1}
                </div>
                <div style={{ width: 44, height: 44, borderRadius: "50%", background: "#111", overflow: "hidden", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", border: i < 3 ? `2px solid ${i === 0 ? "#ffd700" : i === 1 ? "#c0c0c0" : "#cd7f32"}` : "none" }}>
                  {fan.userPhoto ? <img src={fan.userPhoto} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <span>👤</span>}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: i === 0 ? "#ffd700" : "#fff" }}>{fan.userName}</div>
                  <div style={{ fontSize: 11, color: "#555" }}>Last listened {new Date(fan.lastListened || Date.now()).toLocaleDateString()}</div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: "#1db954" }}>{fan.plays}</div>
                  <div style={{ fontSize: 10, color: "#444" }}>plays</div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* EDIT PROFILE TAB */}
        {tab === "edit" && (
          <div>
            <div style={{ fontFamily: "'Syne',sans-serif", fontSize: 16, fontWeight: 800, marginBottom: 16 }}>Edit Profile</div>

            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 11, color: "#555", marginBottom: 8, textTransform: "uppercase" }}>Profile Photo</div>
              <label style={{ cursor: "pointer", display: "block" }}>
                <div style={{ width: 80, height: 80, borderRadius: "50%", background: "#111", overflow: "hidden", border: "2px dashed #2a2a2a", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  {photoPreview ? <img src={photoPreview} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <span style={{ fontSize: 28 }}>🎤</span>}
                </div>
                <input type="file" accept="image/*" style={{ display: "none" }} onChange={e => { const f = e.target.files[0]; if (f) { setPhotoFile(f); setPhotoPreview(URL.createObjectURL(f)); }}} />
              </label>
            </div>

            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 11, color: "#555", marginBottom: 8, textTransform: "uppercase" }}>Cover Photo</div>
              <label style={{ cursor: "pointer", display: "block" }}>
                <div style={{ width: "100%", height: 80, borderRadius: 10, background: coverPreview ? `url(${coverPreview}) center/cover` : "#111", border: "2px dashed #2a2a2a", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  {!coverPreview && <span style={{ fontSize: 13, color: "#555" }}>+ Add Cover Photo</span>}
                </div>
                <input type="file" accept="image/*" style={{ display: "none" }} onChange={e => { const f = e.target.files[0]; if (f) { setCoverFile(f); setCoverPreview(URL.createObjectURL(f)); }}} />
              </label>
            </div>

            {[{ label: "Artist Name", value: artistName, set: setArtistName, placeholder: "Your artist name" }, { label: "Bio", value: bio, set: setBio, placeholder: "Tell fans about yourself...", multi: true }].map(f => (
              <div key={f.label} style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 11, color: "#555", marginBottom: 6, textTransform: "uppercase" }}>{f.label}</div>
                {f.multi ? (
                  <textarea value={f.value} onChange={e => f.set(e.target.value)} placeholder={f.placeholder} rows={3} style={{ width: "100%", padding: "11px 14px", borderRadius: 10, border: "1px solid #2a2a2a", background: "#0a0a0a", color: "#fff", fontFamily: "'DM Sans',sans-serif", fontSize: 14, outline: "none", resize: "vertical", boxSizing: "border-box" }} />
                ) : (
                  <input value={f.value} onChange={e => f.set(e.target.value)} placeholder={f.placeholder} style={{ width: "100%", padding: "11px 14px", borderRadius: 10, border: "1px solid #2a2a2a", background: "#0a0a0a", color: "#fff", fontFamily: "'DM Sans',sans-serif", fontSize: 14, outline: "none", boxSizing: "border-box" }} />
                )}
              </div>
            ))}

            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 11, color: "#555", marginBottom: 8, textTransform: "uppercase" }}>Genre</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {["Pop", "Hip-Hop", "R&B", "Rock", "Electronic", "K-Pop", "Myanmar Pop", "Myanmar Rock", "Christian", "Indie", "Other"].map(g => (
                  <button key={g} onClick={() => setGenre(g)} style={{ padding: "6px 14px", borderRadius: 50, border: `1px solid ${genre === g ? "#1db954" : "#2a2a2a"}`, background: genre === g ? "rgba(29,185,84,0.1)" : "transparent", color: genre === g ? "#1db954" : "#666", fontSize: 12, cursor: "pointer" }}>{g}</button>
                ))}
              </div>
            </div>

            <button onClick={saveProfile} disabled={saving} style={{ width: "100%", padding: 14, borderRadius: 50, border: "none", background: saving ? "#333" : "#1db954", color: saving ? "#777" : "#000", fontSize: 14, fontWeight: 700, cursor: saving ? "not-allowed" : "pointer" }}>
              {saving ? "Saving..." : "Save Profile"}
            </button>
          </div>
        )}
      </div>

      {/* Upload Song Modal */}
      {showUpload && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.92)", zIndex: 300, display: "flex", alignItems: "flex-end" }} onClick={() => !uploading && setShowUpload(false)}>
          <div style={{ background: "#141414", borderRadius: "20px 20px 0 0", padding: 20, width: "100%", border: "1px solid #2a2a2a", maxHeight: "85vh", overflowY: "auto" }} onClick={e => e.stopPropagation()}>
            <div style={{ fontFamily: "'Syne',sans-serif", fontSize: 18, fontWeight: 800, marginBottom: 16 }}>Upload Song</div>

            <input value={songTitle} onChange={e => setSongTitle(e.target.value)} placeholder="Song title *" style={{ width: "100%", padding: "11px 14px", borderRadius: 10, border: "1px solid #2a2a2a", background: "#0a0a0a", color: "#fff", fontFamily: "'DM Sans',sans-serif", fontSize: 14, outline: "none", marginBottom: 12, boxSizing: "border-box" }} />

            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 12, color: "#555", marginBottom: 6 }}>Audio File (MP3, WAV) *</div>
              <label style={{ display: "block", padding: "14px", borderRadius: 10, border: `1px dashed ${songFile ? "#1db954" : "#2a2a2a"}`, background: "#0a0a0a", cursor: "pointer", textAlign: "center" }}>
                <span style={{ fontSize: 13, color: songFile ? "#1db954" : "#555" }}>{songFile ? `✓ ${songFile.name}` : "Tap to select audio file"}</span>
                <input type="file" accept="audio/*,video/mp4" style={{ display: "none" }} onChange={e => setSongFile(e.target.files[0])} />
              </label>
            </div>

            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 12, color: "#555", marginBottom: 6 }}>Cover Art (Optional)</div>
              <label style={{ display: "block", cursor: "pointer" }}>
                <div style={{ width: 80, height: 80, borderRadius: 10, background: songCoverPreview ? `url(${songCoverPreview}) center/cover` : "#0a0a0a", border: "1px dashed #2a2a2a", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  {!songCoverPreview && <span style={{ fontSize: 24 }}>🖼️</span>}
                </div>
                <input type="file" accept="image/*" style={{ display: "none" }} onChange={e => { const f = e.target.files[0]; if (f) { setSongCoverFile(f); setSongCoverPreview(URL.createObjectURL(f)); }}} />
              </label>
            </div>

            {uploadProgress && <div style={{ textAlign: "center", color: "#1db954", fontSize: 13, marginBottom: 12 }}>{uploadProgress}</div>}

            <button onClick={uploadSong} disabled={uploading || !songTitle.trim() || !songFile} style={{ width: "100%", padding: 14, borderRadius: 50, border: "none", background: uploading || !songTitle.trim() || !songFile ? "#333" : "#1db954", color: uploading || !songTitle.trim() || !songFile ? "#777" : "#000", fontSize: 14, fontWeight: 700, cursor: "pointer" }}>
              {uploading ? "Uploading..." : "Upload Song"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}